import * as React from 'react';
import { CheckCircle2, Copy, Link2, Share2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useToast,
} from '@finmin/ui';
import { channelPartnerSignupLink } from '../app-config';
import { channelPartnerShareText } from '../format';

/**
 * The CP code to share with new customers — copy, copy-link (where the app
 * hosts customer signup) and native share with a clipboard fallback.
 */
export function ChannelPartnerCodeCard({ code }: { code: string | null }) {
  const { toast } = useToast();
  const [copied, setCopied] = React.useState<'code' | 'link' | null>(null);
  const link = code ? channelPartnerSignupLink(code) : null;

  async function copy(kind: 'code' | 'link', text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      toast.success(kind === 'code' ? 'Code copied' : 'Invite link copied');
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy', 'Copy it manually instead.');
    }
  }

  async function share() {
    if (!code) return;
    const text = channelPartnerShareText(code);
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: 'FinminOS', text, ...(link ? { url: link } : {}) });
        return;
      } catch {
        // Share sheet dismissed — fall back to the clipboard.
      }
    }
    await copy('code', link ? `${text}\n${link}` : text);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Share2 className="h-4 w-4 text-muted-foreground" aria-hidden />
          Your Channel Partner code
        </CardTitle>
        <CardDescription>
          New customers enter this code in the referral code field when they sign up. It is
          separate from your ordinary referral code.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border-2 border-dashed border-forest/40 bg-mint px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-forest/70">CP code</p>
          <p className="mt-0.5 break-all font-mono text-2xl font-bold tracking-[0.15em] text-forest">
            {code ?? '—'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!code}
            onClick={() => code && void copy('code', code)}
          >
            {copied === 'code' ? (
              <CheckCircle2 className="h-4 w-4" aria-hidden />
            ) : (
              <Copy className="h-4 w-4" aria-hidden />
            )}
            {copied === 'code' ? 'Copied' : 'Copy code'}
          </Button>
          {link && (
            <Button size="sm" variant="outline" onClick={() => void copy('link', link)}>
              {copied === 'link' ? (
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
              ) : (
                <Link2 className="h-4 w-4" aria-hidden />
              )}
              {copied === 'link' ? 'Link copied' : 'Copy invite link'}
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={!code} onClick={() => void share()}>
            <Share2 className="h-4 w-4" aria-hidden />
            Share
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
